/**
 * Stage Recovery Report — Data Ops
 * Route: /data-ops/stage-report
 * Analyzes the "Other / Unstaged" asset bucket, categorizes it, estimates
 * AI recoverability (with a live Perplexity sample), and offers one-click
 * recovery / flag-for-review actions.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  FlaskConical, Loader2, Sparkles, Download, ShieldCheck, Flag,
  CheckCircle2, HelpCircle, AlertTriangle, TrendingUp, Building2, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_STYLE = {
  platform:              { color: 'text-slate-600', bg: 'bg-slate-100', Icon: FlaskConical },
  academic:              { color: 'text-orange-600', bg: 'bg-orange-50', Icon: Building2 },
  diagnostics:           { color: 'text-cyan-600', bg: 'bg-cyan-50', Icon: AlertTriangle },
  commercial_uncaptured: { color: 'text-emerald-600', bg: 'bg-emerald-50', Icon: CheckCircle2 },
  clinical_in_text:      { color: 'text-violet-600', bg: 'bg-violet-50', Icon: TrendingUp },
  discovery_research:    { color: 'text-blue-600', bg: 'bg-blue-50', Icon: Sparkles },
  truly_unknown:         { color: 'text-slate-500', bg: 'bg-slate-100', Icon: HelpCircle },
  other:                 { color: 'text-amber-600', bg: 'bg-amber-50', Icon: AlertTriangle },
};

export default function StageRecoveryReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [acting, setActing] = useState('');
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  // Live progress for the stage recovery job — this is what "Recover
  // high-confidence stages now" starts (previously only a toast showed).
  const fetchJob = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/data-ops/stage-web-backfill/status`, { withCredentials: true });
      const j = data.job || null;
      setJob(j);
      return j;
    } catch { return null; }
  }, []);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      const j = await fetchJob();
      if (stopped) return;
      if (j && ['queued', 'running'].includes(j.status)) {
        pollRef.current = setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { stopped = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [fetchJob]);

  const startJobPolling = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const tick = async () => {
      const j = await fetchJob();
      if (j && ['queued', 'running'].includes(j.status)) pollRef.current = setTimeout(tick, 3000);
    };
    tick();
  };

  const generate = async () => {
    setLoading(true); setProbe(null);
    try {
      const { data } = await axios.get(`${API}/api/data-ops/stage-report/analyze`, { withCredentials: true });
      setReport(data);
    } catch (e) {
      toast.error('Failed to generate report');
    } finally { setLoading(false); }
  };

  const runProbe = async () => {
    setProbing(true);
    try {
      const { data } = await axios.post(`${API}/api/data-ops/stage-report/sample-probe`,
        { sample_size: 25 }, { withCredentials: true });
      setProbe(data);
      toast.success(`Sampled ${data.sampled} assets — ${Math.round(data.recovery_rate * 100)}% recovered`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sample probe failed');
    } finally { setProbing(false); }
  };

  const recover = async () => {
    setActing('recover');
    try {
      const { data } = await axios.post(`${API}/api/data-ops/stage-report/recover-high-confidence`, {}, { withCredentials: true });
      toast[data.started ? 'success' : 'info'](data.started
        ? 'AI stage recovery started — live progress shown below.'
        : (data.reason || 'A recovery job is already running.'));
      startJobPolling();
    } catch (e) { toast.error('Could not start recovery'); }
    finally { setActing(''); }
  };

  const excludeOutOfScope = async () => {
    setActing('exclude');
    try {
      const { data } = await axios.post(`${API}/api/data-ops/stage-report/exclude-out-of-scope`,
        { dry_run: false }, { withCredentials: true });
      const cats = Object.entries(data.by_category || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
      toast.success(`Removed ${data.deleted || 0} out-of-scope assets (${cats}). Assets: ${data.before_assets} → ${data.after_assets}`, { duration: 10000 });
      if (data.protected_skipped > 0) toast.info(`${data.protected_skipped} assets kept — actively used in client review workflow.`);
      generate();
    } catch (e) { toast.error('Could not remove out-of-scope assets'); }
    finally { setActing(''); }
  };

  const flagReview = async () => {
    setActing('flag');
    try {
      const { data } = await axios.post(`${API}/api/data-ops/stage-report/flag-review`,
        { category: 'truly_unknown' }, { withCredentials: true });
      toast.success(`Flagged ${data.flagged} "Truly Unknown" assets for manual review.`);
    } catch (e) { toast.error('Could not flag assets'); }
    finally { setActing(''); }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ report, probe }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `stage-recovery-report-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const recommendation = () => {
    if (!report) return null;
    const rate = probe ? Math.round(probe.recovery_rate * 100) : null;
    const excl = report.likely_excludable;
    const rec = report.recoverable_estimate;
    return (
      <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
        <li><strong>Auto-recover with AI:</strong> {rec.toLocaleString()} assets have recoverable signal
          {rate !== null && <> — the live sample recovered <strong>{rate}%</strong>, projecting ~<strong>{probe.projected_recoverable.toLocaleString()}</strong> automatically classifiable</>}.
          Run "Recover high-confidence stages now".</li>
        <li><strong>Flag for manual review:</strong> the "Truly Unknown" and "Other" buckets that AI cannot resolve should be flagged for a human pass.</li>
        <li><strong>Exclude from out-licensing scope:</strong> ~{excl.toLocaleString()} assets (Platform / Academic / Diagnostics) are outside the therapeutic out-licensing model and can be excluded rather than recovered.</li>
      </ul>
    );
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-6" data-testid="stage-recovery-report-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <FlaskConical className="w-6 h-6 text-violet-600" /> Stage Recovery Report
          </h1>
          <p className="text-sm text-slate-500 mt-1">Analyze the “Other / Unstaged” assets, estimate AI recoverability, and decide the next step.</p>
        </div>
        <div className="flex gap-2">
          {report && (
            <Button variant="outline" onClick={exportJson} data-testid="export-report-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export JSON
            </Button>
          )}
          <Button onClick={generate} disabled={loading} data-testid="generate-report-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {report ? 'Regenerate' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {/* Live recovery job progress */}
      {job && (
        <Card className={`mb-6 ${['queued', 'running'].includes(job.status) ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`} data-testid="recovery-job-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="font-semibold text-slate-800 flex items-center gap-2">
                {['queued', 'running'].includes(job.status)
                  ? <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                Stage recovery job — <span className="capitalize">{job.status}</span>
              </p>
              <a href="/data-ops/stage-backfill" className="text-xs text-sky-600 hover:underline">Open Phase / Stage Backfill →</a>
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span data-testid="job-progress-text">{(job.processed || 0).toLocaleString()} / {(job.total || 0).toLocaleString()} processed</span>
                <span>{(job.updated || 0).toLocaleString()} stages recovered · {(job.failed || 0).toLocaleString()} not found</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${job.total ? Math.round(((job.processed || 0) / job.total) * 100) : 0}%` }} />
              </div>
              {job.current && ['queued', 'running'].includes(job.status) && (
                <p className="text-[11px] text-slate-400 mt-1.5 truncate">Currently: {job.current}</p>
              )}
              {job.error && <p className="text-[11px] text-rose-600 mt-1.5">{job.error}</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {!report && !loading && (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500">
          Click <strong>Generate Report</strong> to analyze every unstaged asset in this environment.
        </CardContent></Card>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total Unstaged', value: report.total_unstaged, cls: 'text-slate-900' },
              { label: 'Likely Recoverable', value: report.recoverable_estimate, cls: 'text-emerald-600' },
              { label: 'Likely Excludable (out of scope)', value: report.likely_excludable, cls: 'text-slate-500' },
            ].map((s) => (
              <Card key={s.label}><CardContent className="p-5">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{s.label}</p>
                <p className={`text-3xl font-bold ${s.cls}`} style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`stat-${s.label}`}>{s.value.toLocaleString()}</p>
              </CardContent></Card>
            ))}
          </div>

          {/* AI sample */}
          <Card className="mb-6 border-violet-200 bg-violet-50/40"><CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-semibold text-slate-800 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-600" /> Live AI recoverability estimate</p>
                <p className="text-xs text-slate-500 mt-0.5">Runs Perplexity web search on a random 25-asset sample to measure the real recovery rate.</p>
              </div>
              <Button onClick={runProbe} disabled={probing} className="bg-violet-600 hover:bg-violet-700" data-testid="run-sample-probe-btn">
                {probing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1.5" />}
                Run 25-asset AI sample
              </Button>
            </div>
            {probe && (
              <div className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <Stat label="Sampled" v={probe.sampled} />
                  <Stat label="Recovered" v={probe.recovered} />
                  <Stat label="Recovery rate" v={`${Math.round(probe.recovery_rate * 100)}%`} accent />
                  <Stat label="Projected recoverable" v={probe.projected_recoverable} accent />
                </div>
                {Object.keys(probe.stage_distribution || {}).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {Object.entries(probe.stage_distribution).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="bg-white">{k}: {v}</Badge>
                    ))}
                  </div>
                )}
                <div className="max-h-64 overflow-y-auto border rounded bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-slate-500">
                      <th className="p-2">Asset</th><th className="p-2">Company</th><th className="p-2">Recovered</th><th className="p-2">Conf.</th>
                    </tr></thead>
                    <tbody>
                      {probe.results.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2 text-slate-700">{r.asset_name}</td>
                          <td className="p-2 text-slate-500">{r.company_name}</td>
                          <td className="p-2">{r.recovered_stage
                            ? <Badge className="bg-emerald-100 text-emerald-700">{r.recovered_stage}</Badge>
                            : <span className="text-slate-400">not found</span>}</td>
                          <td className="p-2 text-slate-500">{r.confidence || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent></Card>

          {/* Categories */}
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Category breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {report.categories.map((c) => {
              const st = CAT_STYLE[c.key] || CAT_STYLE.other;
              return (
                <Card key={c.key} data-testid={`category-${c.key}`}><CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg ${st.bg} flex items-center justify-center shrink-0`}>
                      <st.Icon className={`w-4 h-4 ${st.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800">{c.label}</span>
                        {c.recoverable
                          ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Recoverable</Badge>
                          : <Badge className="bg-slate-200 text-slate-600 text-[10px]">Likely exclude</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{c.why}</p>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span className="font-bold text-slate-900">{c.asset_count.toLocaleString()}</span>
                        <span className="text-slate-400 text-xs self-center">assets · {c.company_count.toLocaleString()} companies</span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {c.examples.map((e, i) => (
                          <div key={i} className="text-[11px] text-slate-500 truncate">
                            • <span className="text-slate-700">{e.asset_name}</span> — {e.company_name}
                            <span className="text-slate-400"> ({e.raw_stage})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent></Card>
              );
            })}
          </div>

          {/* Recommendation + actions */}
          <Card className="mb-6"><CardContent className="p-5">
            <p className="font-semibold text-slate-800 mb-2">Recommendation</p>
            {recommendation()}
            <div className="flex gap-2 mt-4 flex-wrap">
              <Button onClick={recover} disabled={acting === 'recover'} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recover-btn">
                {acting === 'recover' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}
                Recover high-confidence stages now
              </Button>
              <Button onClick={flagReview} disabled={acting === 'flag'} variant="outline" data-testid="flag-review-btn">
                {acting === 'flag' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Flag className="w-4 h-4 mr-1.5" />}
                Flag “Truly Unknown” for manual review
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={acting === 'exclude' || !report.likely_excludable} variant="outline"
                    className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid="exclude-oos-btn">
                    {acting === 'exclude' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                    Remove {report.likely_excludable.toLocaleString()} out-of-scope assets
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {report.likely_excludable.toLocaleString()} out-of-scope assets?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Deletes the Platform / Academic / Diagnostics unstaged assets from the database permanently.
                      They are archived to <code>excluded_assets_archive</code> first, and any asset used in the
                      client review workflow is automatically kept. Dashboard metrics recount instantly.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="exclude-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={excludeOutOfScope} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="exclude-confirm-btn">
                      Remove permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div className="bg-white rounded border p-3">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${accent ? 'text-violet-600' : 'text-slate-900'}`}>{v}</p>
    </div>
  );
}
